<template>
<div>
  <loading-panel :loading="loading"></loading-panel>

  <!-- Empty state -->
  <div v-if="!loading && drafts.length === 0" class="row justify-content-center">
    <div class="col-lg-6 col-md-8 col-10">
      <div class="draft-empty gt-empty">
        <div class="draft-empty__icon gt-empty__icon">
          <i class="tim-icons icon-notes"></i>
        </div>
        <h4 class="draft-empty__title gt-empty__title">No drafts yet</h4>
        <p class="draft-empty__subtitle gt-empty__subtitle">
          Click <strong>New form</strong> above to start tracking an event.
        </p>
      </div>
    </div>
  </div>

  <!-- Draft list -->
  <div v-if="drafts.length > 0" class="row">
    <div class="col-lg-8 col-md-10 col-12 ml-auto mr-auto">
      <div class="draft-list">
        <div
          v-for="draft in drafts"
          :key="draft.id"
          class="draft-item gt-list-item"
          :class="{ 'draft-item--selected gt-list-item--selected': selectedDraft && selectedDraft.id === draft.id }"
          @click="selectDraft(draft)"
          tabindex="0"
          @keydown.enter="selectDraft(draft)"
          @keydown.space.prevent="selectDraft(draft)"
        >
          <img v-if="draft.game_image_url" v-twin-src="draft.game_image_url" class="draft-item__img" alt="" />
          <div class="draft-item__img-placeholder" v-else>
            <i class="tim-icons icon-shape-star"></i>
          </div>
          <div class="draft-item__content">
            <div class="draft-item__header">
              <div class="draft-item__title-row">
                <i v-if="selectedDraft && selectedDraft.id === draft.id"
                   class="tim-icons icon-check-2 draft-item__check"></i>
                <span class="draft-item__name">
                  {{ draft.game_name || 'Empty draft' }}
                </span>
              </div>
              <button
                @click.stop="deleteDraftPrompt(draft)"
                class="draft-item__delete">
                <i class="tim-icons icon-simple-remove"></i>
              </button>
            </div>
            <div class="draft-item__meta">
              <span v-if="draft.booking_date" class="draft-item__meta-tag text-white">
                <i class="tim-icons icon-calendar-60"></i>
                {{ getFormattedDate(draft.booking_date) }}, {{ getTime(draft.start_time) }}
              </span>
              <span class="draft-item__meta-tag text-white">
                <i class="tim-icons icon-watch-time"></i>
                Created {{ getLocalDate(draft.time_created) }}
              </span>
              <span v-if="draft.time_updated" class="draft-item__meta-tag text-white">
                <i class="tim-icons icon-refresh-02"></i>
                Updated {{ getLocalDate(draft.time_updated) }}
              </span>
              <span v-if="slotNotice(draft)" class="draft-item__meta-tag draft-item__meta-tag--warning">
                <i class="tim-icons icon-alert-circle-exc"></i>
                {{ slotNotice(draft) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Validation error -->
      <div v-if="error" class="draft-error gt-error-banner mt-3">
        <i class="tim-icons icon-alert-circle-exc"></i>
        {{ errorMessage || 'Select a draft or create a new one to continue' }}
      </div>
    </div>
  </div>

  <base-alert-dialog
    :show="alert.show"
    :title="alert.title"
    :message="alert.message"
    variant="danger"
    :dismissible="!alert.redirect"
    @confirm="onAlertConfirm"
    @cancel="onAlertCancel"
  ></base-alert-dialog>
</div>
</template>

<script>
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import swal from "sweetalert2";
import {Auth} from "aws-amplify";
import {time, formattedDate, localDate, localTime, dayOfWeek} from "@/plugins/dateFormatter";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";


export default {
  name: "StartNewForm",
  props: {
    gameInstanceId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      k: 0,
      drafts: [],
      error: false,
      errorMessage: '',
      selectedDraft: '',
      // Sequence guard for overlapping list fetches: only the latest response
      // is applied. lastDraftsPromise lets a caller superseded mid-flight wait
      // for the newest fetch instead of acting on a stale one.
      draftsSeq: 0,
      lastDraftsPromise: null,
      alert: { show: false, title: '', message: '', redirect: false },
      model: {
        draft: {
          id: '',
          name: ''

        }
      },
    };
  },
  methods: {
    draftKey(key) {
      if (!this.gameInstanceId) {
        console.warn('draftKey called with no gameInstanceId for key:', key)
        return key + '_NONE'
      }
      return key + '_' + this.gameInstanceId
    },
    slotNotice(draft) {
      // draft.status is the linked booking slot's status from getDrafts. A
      // slot can be completed or released by flows that never touch the draft
      // (simple tracker, schedule tooling, payment expiry), so surface that
      // state and let the host decide whether to resume or delete the draft.
      if (!draft.booking_date) return null
      if (draft.status === 'completed') return 'Booking completed'
      if (draft.status === 'available' || draft.status === 'expired') return 'Booking cancelled'
      return null
    },
    selectDraft(draft) {
      // In-memory selection only. The host refetches the list and calls
      // refreshSelection so the copy written to localStorage is always the
      // server row, never the entry cached from an earlier fetch.
      this.selectedDraft = draft
      this.error = false
      this.errorMessage = ''
      this.$emit('draft-clicked', draft)
    },
    async refreshSelection(id) {
      let status = await this.getDrafts()
      while (status === 'superseded') {
        status = await this.lastDraftsPromise
      }
      if (status !== 'ok') {
        // The fetch failed, so the clicked card was never confirmed. Put the
        // highlight back on the draft the host is actually on (or nothing)
        // so the selection and gameInstanceId cannot disagree.
        this.selectedDraft = this.drafts.find(d => String(d.id) === String(this.gameInstanceId)) || ''
        return null
      }
      // The user clicked another card while this fetch was in flight; the
      // host re-runs for that click, so this one must not confirm anything.
      if (!this.selectedDraft || String(this.selectedDraft.id) !== String(id)) return null
      let fresh = this.drafts.find(d => String(d.id) === String(id))
      if (fresh) {
        // getDrafts already replaced selectedDraft with this row and wrote it
        return fresh
      }
      this.selectedDraft = ''
      localStorage.removeItem('draft_' + id)
      if (this.gameInstanceId != null) {
        this.$emit('update:game-instance-id', null)
      }
      this.error = true
      this.errorMessage = 'This draft no longer exists. Select another draft or create a new one.'
      return null
    },
    onAlertConfirm() {
      let redirect = this.alert.redirect
      this.alert = { show: false, title: '', message: '', redirect: false }
      if (redirect) {
        this.$router.push('/')
      }
    },
    onAlertCancel() {
      // Dismissing only hides; the 401 alert is non-dismissible so its
      // redirect can only happen through OK
      this.alert = { show: false, title: '', message: '', redirect: false }
    },
    setDraft(){
      if(this.selectedDraft){
        this.model.draft = this.selectedDraft
        // Emit first so parent updates gameInstanceId
        this.$emit('update:game-instance-id', this.selectedDraft.id)
        // Use the draft's own ID for the key directly, because the prop
        // won't reflect the new gameInstanceId until the next tick
        let key = this.selectedDraft.id ? 'draft_' + this.selectedDraft.id : 'draft'
        localStorage.setItem(key, JSON.stringify(this.selectedDraft))
      }
    },
    nextStep(){
      return this.selectedDraft.current_step - 1
    },
    getLocalDate(d){
      return formatGroupDate(d)
    },
    getLocalTime(d){
      return formatGroupTime(d)
    },
    getDayOfWeek(d){
      if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
        return dayOfWeek(d) + ', '
      }
      return ''
    },
    getFormattedDate(d){
      return this.getDayOfWeek(d) + formattedDate(this.date_format, d)
    },
    getTime(d) {
      return time(this.time_format, d)
    },
    async getAuthToken() {
      // Rejects on a failed session so callers see an error instead of a
      // promise that never settles
      let data = await Auth.currentSession()
      return data.idToken.jwtToken
    },
    async getDrafts(){
      const seq = ++this.draftsSeq
      const run = async () => {
        this.loading = true
        let companyId = localStorage.getItem('companyId')
        let url = API_LOCATION + 'companies/' + companyId + '/games/instances/drafts'

        let response
        try {
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          response = await axios.get(url, config)
        } catch (err) {
          if (seq !== this.draftsSeq) return 'superseded'
          this.loading = false
          if (err.response && err.response.status == 401) {
            this.alert = {
              show: true,
              title: 'Error',
              message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              redirect: true
            }
          } else {
            this.alert = { show: true, title: 'Error', message: 'Something went wrong', redirect: false }
          }
          return 'error'
        }

        // A newer fetch is in flight; let it apply the list
        if (seq !== this.draftsSeq) return 'superseded'

        this.drafts = response.data
        this.k++

        // Validate existing selection against fresh data
        if(this.selectedDraft && this.selectedDraft.hasOwnProperty('id')){
          let match = this.drafts.find(d => String(d.id) === String(this.selectedDraft.id))
          if(match){
            this.selectedDraft = match
            this.setDraft()
          } else if (!this.gameInstanceId) {
            // Only clear selection if there's no active gameInstanceId.
            // A stale getDrafts response (fired before createNewDraft)
            // might not include the newly created draft yet.
            localStorage.removeItem('draft_' + this.selectedDraft.id)
            this.selectedDraft = ''
            this.$emit('update:game-instance-id', null)
          }
        }

        this.$emit('on-get-drafts', this.drafts)
        this.loading = false

        // If parent already has a gameInstanceId (e.g. from URL), select that draft
        if (!this.selectedDraft && this.gameInstanceId && this.drafts.length > 0) {
          let match = this.drafts.find(d => String(d.id) === String(this.gameInstanceId))
          if (match) {
            this.$nextTick(() => {
              this.selectedDraft = match
              this.error = false
              this.setDraft()
              this.$emit('draft-selected', match)
            })
            return 'ok'
          }
        }

        // No auto-select — user must explicitly pick a draft or create a new one.
        // Auto-selecting the first draft caused cross-device collisions when
        // the same user had multiple computers open (all would silently share
        // the same draft, overwriting each other's data).
        return 'ok'
      }
      this.lastDraftsPromise = run()
      return this.lastDraftsPromise
    },
    validate() {
      if(this.selectedDraft && String(this.selectedDraft.id) === String(this.gameInstanceId)){
        this.error = false
        this.errorMessage = ''
        return true
      }
      this.error = true
      this.errorMessage = this.selectedDraft
        ? 'The selected draft did not finish loading. Select it again to continue.'
        : ''
      return false
    },
    deleteDraftPrompt(d){
      swal({
        title: 'Are you sure?',
        text: "The draft will be permanently deleted.",
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete'
      }).then((response)=> {
        if(response.value == true){
          this.deleteDraft(d)
        }
      })
    },
    async deleteDraft(d){
      this.loading = true
      let companyId = localStorage.getItem('companyId')

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + companyId + '/games/instances/' + d.id + '/drafts'
      axios.delete(url, config)
        .then(response => {
          this.loading = false
          this.selectedDraft = ''
          localStorage.removeItem(this.draftKey('draft'))
          this.$emit('update:game-instance-id', null)
          this.getDrafts()
        })
        .catch(err => {
          this.loading = false
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    async createInstance(){
      this.loading = true
      let companyId = localStorage.getItem('companyId')

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + companyId + '/games/instances/drafts'
      let data = {}
      axios.post(url, data, config)
        .then(response => {
          this.loading = false
          this.validate()
        })
        .catch(err => {
          this.loading = false
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          }  else if (err.response.status == 502){
            swal({
              title: 'Ooops!',
              text: "Duplicate submission",
              type: 'warning',
              confirmButtonColor: '#1d8cf8',
              confirmButtonText: 'OK'
            })
          } else {
            swal({
              title: 'Ooops!',
              text: "There were some issues with your submission. Please check your form and try again!",
              type: 'warning',
              confirmButtonColor: '#1d8cf8',
              confirmButtonText: 'OK'
            })
          }

        })
    },
  },
  mounted() {
    this.getDrafts()
  }
}
</script>

<style scoped>
/* ========================================
   Draft list
   ======================================== */
.draft-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ========================================
   Draft item — layout only (visual base from gt-list-item)
   ======================================== */
.draft-item {
  display: flex;
  align-items: center;
  gap: 14px;
}

/* ========================================
   Content layout
   ======================================== */
.draft-item__img {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.draft-item__img-placeholder {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.25);
  font-size: 18px;
}

.draft-item__content {
  flex: 1;
  min-width: 0;
}

.draft-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.draft-item__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.draft-item__check {
  color: #e14eca;
  font-size: 13px;
  flex-shrink: 0;
}

.draft-item__name {
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.draft-item--selected .draft-item__name {
  color: #e14eca;
}

/* ========================================
   Delete button (hover reveal)
   ======================================== */
.draft-item__delete {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.15s ease;
}

.draft-item__delete:hover {
  color: #fd5d93;
}

/* ========================================
   Metadata tags
   ======================================== */
.draft-item__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
}

.draft-item__meta-tag {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.draft-item__meta-tag i {
  font-size: 11px;
}

.draft-item__meta-tag--warning {
  color: #ff8d72;
}

/* ========================================
   Empty state — margin only (visual base from gt-empty)
   ======================================== */
.draft-empty {
  margin-top: 12px;
}

/* ========================================
   Responsive
   ======================================== */
@media (max-width: 576px) {
  .draft-item__meta {
    gap: 6px;
  }

  .draft-item__meta-tag {
    font-size: 11px;
  }
}
</style>

<!-- Light mode overrides (unscoped) — base/hover/selected handled by gt-list-item -->
<style>
.white-content .draft-item__name {
  color: #344675;
}

.white-content .draft-item--selected .draft-item__name {
  color: #e14eca;
}

.white-content .draft-item__meta-tag {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .draft-item__meta-tag--warning {
  color: #e8593c;
}

.white-content .draft-item__delete {
  color: rgba(0, 0, 0, 0.35);
}

.white-content .draft-item__delete:hover {
  color: #fd5d93;
}

.white-content .draft-item__img {
  border-color: rgba(0, 0, 0, 0.1);
}

.white-content .draft-item__img-placeholder {
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.2);
}

</style>
